/* eslint-disable import/no-default-export */
import classNames from 'classnames';
import React from 'react';

import RouterLink from 'app/src/components/RouterLink/RouterLink';
import { forNamespace } from '@trello/i18n';
import { EnterpriseIcon } from '@trello/nachos/icons/enterprise';
import { RemoveIcon } from '@trello/nachos/icons/remove';
import { AddIcon } from '@trello/nachos/icons/add';
import styles from './collapsible.less';

const format = forNamespace('');

interface AvatarProps {
  readonly avatarURL?: string;
  readonly icon?: JSX.Element;
}

export interface AnimateProps {
  readonly enabled?: boolean;
  readonly speed?: 'Fast' | 'Medium' | 'Slow';
  readonly contentHeight?: string;
}

export interface CollapsibleProps extends AvatarProps {
  readonly id: string;
  readonly categoryId: string;
  readonly title: string;
  readonly open: boolean;
  readonly headerClass: string;
  readonly tabIndex?: number;
  readonly toggleClass: string;
  readonly titleClass?: string;
  readonly collapsibleClass?: string;
  readonly onToggleClick?: () => void;
  readonly url?: string;
  readonly showPremiumIndicator: boolean;
  readonly trackToggleClick?: (closing: boolean) => void;
  readonly trackURLClick?: (closing: boolean) => void;
  readonly animate?: AnimateProps;
}

const TeamAvatar = ({ avatarURL, icon }: AvatarProps) => {
  if (avatarURL) {
    return (
      <span
        className={styles.avatar}
        style={{ backgroundImage: `url(${avatarURL})` }}
      />
    );
  } else if (icon) {
    return React.cloneElement(icon, {
      size: 'small',
      color: 'quiet',
      dangerous_className: styles.icon,
    });
  }

  return null;
};

const getInlineStylesForChildrenCollapsibleState = (
  open: boolean,
  animateProps?: AnimateProps,
) => {
  if (animateProps && animateProps.enabled) {
    const animateState = {
      maxHeight: '0px',
    };

    if (open && animateProps.contentHeight) {
      animateState.maxHeight = animateProps.contentHeight;
    } else if (open) {
      animateState.maxHeight = '100px';
    }

    return animateState;
  }

  return {};
};

const getClassNameForChildrenCollapsibleState = (
  open: boolean,
  animateProps?: AnimateProps,
) => {
  let containerClass = styles.open;

  if (!open) {
    if (!animateProps || !animateProps.enabled) {
      containerClass = styles.closed;
    }
  }

  let animateClass = '';

  if (animateProps?.enabled && animateProps?.speed) {
    animateClass = styles[`animate${animateProps.speed}`];
  } else if (animateProps?.enabled) {
    animateClass = styles.animateFast;
  }

  return classNames(containerClass, animateClass);
};

export const Collapsible: React.FunctionComponent<CollapsibleProps> = ({
  avatarURL,
  icon,
  title,
  open,
  onToggleClick,
  children,
  collapsibleClass,
  headerClass,
  tabIndex,
  titleClass,
  toggleClass,
  url,
  showPremiumIndicator,
  trackToggleClick,
  trackURLClick,
  animate,
}) => {
  const urlClicked = () => {
    if (trackURLClick) {
      trackURLClick(open);
    }
  };

  const toggleCollapsibleSection = () => {
    if (trackToggleClick) {
      trackToggleClick(open);
    }

    if (onToggleClick) {
      onToggleClick();
    }
  };
  const premiumIcon = !!showPremiumIndicator && (
    <EnterpriseIcon
      size="small"
      color="quiet"
      dangerous_className={classNames(styles.icon, styles.premiumIcon)}
    />
  );

  const titleContents = (
    <span className={styles.titleWrapper}>
      <TeamAvatar avatarURL={avatarURL} icon={icon} />
      <span className={classNames(styles.title, titleClass)}>{title}</span>
      {premiumIcon}
    </span>
  );

  return (
    <div className={classNames(styles.collapsible, collapsibleClass)}>
      <div className={classNames(styles.header, headerClass)}>
        {url ? (
          <RouterLink
            onClick={urlClicked}
            href={url}
            className={styles.headerLink}
          >
            {titleContents}
          </RouterLink>
        ) : (
          <span className={classNames(styles.title, titleClass)}>
            {titleContents}
          </span>
        )}
        <button
          onClick={toggleCollapsibleSection}
          className={classNames(
            {
              [styles.toggleOpen]: open,
            },
            [styles.toggle, toggleClass],
          )}
          tabIndex={tabIndex}
          aria-label={format('collapse')}
        >
          {/* here we handle swapping the components instead of a conditional render, otherwise the boards menu will close because it doesn't think the icon is contained within the boards menu anymore */}
          <RemoveIcon
            size="small"
            color="quiet"
            dangerous_className={styles.toggleIcon}
          />
          <AddIcon
            size="small"
            color="quiet"
            dangerous_className={styles.toggleIcon}
          />
        </button>
      </div>
      <div
        style={getInlineStylesForChildrenCollapsibleState(open, animate)}
        className={getClassNameForChildrenCollapsibleState(open, animate)}
      >
        {children}
      </div>
    </div>
  );
};

export default Collapsible;
